import { useCallback } from "react";
import {
  useInfiniteQuery,
  useMutation,
  useQuery,
  useQueryClient,
  type QueryKey,
  type UseQueryOptions,
} from "@tanstack/react-query";
import { ApiProblemError, createApiClient } from "@guestpilot/api-client";
import type { components } from "@guestpilot/api-client";
import { useToast } from "@guestpilot/ui-kit";

type Schemas = components["schemas"];
export type Schema<K extends keyof Schemas> = Schemas[K];
export { ApiProblemError };

let tokenSource: () => Promise<string | undefined> = async () => undefined;
let onUnauthorized: (() => void) | undefined;

/** The single bearer-injection point: the staff access token of the console's own Entra registration. */
export const api = createApiClient({
  getToken: () => tokenSource(),
  onUnauthorized: () => onUnauthorized?.(),
});

export function configureApi(opts: {
  getToken?: () => Promise<string | undefined>;
  onUnauthorized?: () => void;
}): void {
  if (opts.getToken) tokenSource = opts.getToken;
  if (opts.onUnauthorized) onUnauthorized = opts.onUnauthorized;
}

export const BASE = "/console/api/v1";
export const newKey = (): string =>
  typeof crypto !== "undefined" && "randomUUID" in crypto
    ? crypto.randomUUID()
    : `${Date.now()}-${Math.random().toString(16).slice(2)}`;

type Query = Record<string, string | number | boolean | undefined | null>;

export function useGet<T>(
  path: string,
  query?: Query,
  opts: Partial<Pick<UseQueryOptions<T>, "enabled" | "staleTime">> = {},
) {
  return useQuery<T>({
    queryKey: [path, query ?? {}] as QueryKey,
    queryFn: ({ signal }) => api.get<T>(BASE + path, { ...(query ? { query } : {}), signal }),
    ...opts,
  });
}

export interface PageOf<T> {
  items: T[];
  nextCursor?: string | null;
}

export function useGetPages<T>(path: string, query: Query = {}) {
  const q = useInfiniteQuery<PageOf<T>>({
    queryKey: [path, query, "pages"] as QueryKey,
    initialPageParam: undefined as string | undefined,
    queryFn: ({ pageParam, signal }) =>
      api.get<PageOf<T>>(BASE + path, {
        query: { ...query, ...(pageParam ? { cursor: pageParam as string } : {}) },
        signal,
      }),
    getNextPageParam: (last) => last.nextCursor ?? undefined,
  });
  return { ...q, items: q.data?.pages.flatMap((p) => p.items) };
}

export interface MutationArgs<B> {
  body?: B;
  params?: Record<string, string>;
}

/** A write with a per-click Idempotency-Key; invalidates the listed path prefixes on success. */
export function useWrite<R = unknown, B = unknown>(
  method: "POST" | "PUT" | "PATCH" | "DELETE",
  path: string,
  invalidate: string[] = [],
) {
  const qc = useQueryClient();
  return useMutation<R, Error, MutationArgs<B> | void>({
    mutationFn: async (args) => {
      const a = (args ?? {}) as MutationArgs<B>;
      const url = path.replace(/:(\w+)/g, (_m, k: string) => encodeURIComponent(a.params?.[k] ?? ""));
      return api.request<R>(method, BASE + url, {
        ...(a.body !== undefined ? { body: a.body } : {}),
        idempotencyKey: newKey(),
      });
    },
    onSuccess: async () => {
      await Promise.all(
        invalidate.map((p) =>
          qc.invalidateQueries({
            predicate: (q) => typeof q.queryKey[0] === "string" && (q.queryKey[0] as string).startsWith(p),
          }),
        ),
      );
    },
  });
}

export function fieldErrorsOf(err: unknown): Record<string, string> {
  if (!(err instanceof ApiProblemError)) return {};
  return Object.fromEntries(
    (err.problem.errors ?? []).flatMap((e) => (e.field && e.message ? [[e.field, e.message]] : [])),
  );
}

/** The one place a problem becomes words (the console is English only, D-047): the server's detail, else a generic line. */
export function useHandleApiError() {
  const toast = useToast();
  return useCallback(
    (err: unknown) => {
      if (!(err instanceof ApiProblemError)) return toast.error("Something went wrong. Try again.");
      if (err.problem.code === "validation_failed" && err.problem.errors?.length) return;
      toast.error(err.problem.detail ?? err.problem.title ?? "Something went wrong. Try again.");
    },
    [toast],
  );
}
